import React, { useState, useEffect, useContext } from 'react';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, PointElement, LineElement } from 'chart.js';
import { FaDollarSign, FaBox, FaUsers, FaArrowUp, FaArrowDown, FaChartLine, FaStar } from 'react-icons/fa'; // Ensure all icons are imported
import './Statics.css';
import { UrlContext } from '../../Context/UrlContext';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, PointElement, LineElement);

// --- Component ---

function Statics() {
    const { apiBaseUrl } = useContext(UrlContext);
    const API_BASE_URL = `${apiBaseUrl}/stats/dashboard`;

    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Utility to map string names from the backend to React components
    const getIconComponent = (iconName) => {
        switch (iconName) {
            case 'FaDollarSign': return <FaDollarSign />;
            case 'FaBox': return <FaBox />;
            case 'FaUsers': return <FaUsers />;
            case 'FaChartLine': return <FaChartLine />;
            case 'FaStar': return <FaStar />;
            default: return null;
        }
    };

    // --- Core Data Fetching Logic ---
    const fetchStatsData = async () => {
        setLoading(true);
        setError(null);
        
        try {
            const response = await fetch(API_BASE_URL, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ error: 'Failed to parse error response.' }));
                throw new Error(errorData.error || `HTTP error! Status: ${response.status}`);
            }
            
            const data = await response.json();
            
            setStats(data);
        } catch (err) {
            console.error("Error fetching dashboard data:", err);
            setError(err.message || "Failed to load dashboard data.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStatsData();
    }, [apiBaseUrl]);

    // --- Chart Configurations (Unchanged) ---

    const optionsBar = {
        responsive: true,
        plugins: {
            legend: { position: 'top' },
            title: { display: true, text: 'Monthly Sales Revenue (LKR)' },
        },
    };

    const optionsDoughnut = {
        responsive: true,
        plugins: {
            legend: { position: 'right' },
            title: { display: true, text: 'Product Category Mix (%)' },
        },
    };

    // 1. SALES CHART Data
    const dataSales = {
        labels: stats?.salesByMonth?.labels || [],
        datasets: [
            {
                label: 'Revenue (LKR)',
                data: stats?.salesByMonth?.data || [],
                backgroundColor: 'rgba(79, 70, 229, 0.6)',
                borderColor: 'rgba(79, 70, 229, 1)',
                borderWidth: 1,
            },
        ],
    };
    
    // 2. PRODUCT MIX Chart Data
    const dataProductMix = {
        labels: stats?.productMix?.labels || [],
        datasets: [
            {
                label: 'Sales Mix %',
                data: stats?.productMix?.data || [],
                backgroundColor: ['#10b981', '#4f46e5', '#f59e0b', '#ef4444'],
                hoverOffset: 4,
            },
        ],
    };

    // 3. STOCK LEVELS Chart Data
    const dataStockLevels = {
        labels: stats?.stockLevels?.labels || [],
        datasets: [
            {
                label: 'Units in Stock',
                data: stats?.stockLevels?.data || [],
                backgroundColor: stats?.stockLevels?.data.map(d => d <= 20 ? 'rgba(239, 68, 68, 0.7)' : 'rgba(16, 185, 129, 0.7)'),
                borderColor: stats?.stockLevels?.data.map(d => d <= 20 ? 'rgba(239, 68, 68, 1)' : 'rgba(16, 185, 129, 1)'),
                borderWidth: 1,
            },
        ],
    };

    // --- Render Logic ---

    if (loading) return <div className="statics-loading">Loading Analytics...</div>;
    if (error) return <div className="statics-error">Error: {error}</div>;
    if (!stats || !stats.summary || stats.summary.length === 0) return <div className="statics-error">No data available for the dashboard.</div>;

    return (
        <div className="statics-container">
            <h1 className="statics-title">Business Analytics Dashboard</h1>
            
            {/* 1. Summary Cards */}
            <div className="summary-cards-grid">
                {stats.summary.map((card, index) => (
                    <div key={index} className={`summary-card card-${index}`}>
                        <div className="card-icon-wrapper">
                            {/* FIX: Use the mapping function to display the icon component */}
                            {getIconComponent(card.icon)}
                        </div>
                        <div className="card-content">
                            <span className="card-title">{card.title}</span>
                            <span className="card-value">
                                {card.title === 'Total Revenue' ? `Rs. ${card.value.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : card.value.toLocaleString()}
                            </span>
                        </div>
                        <div className={`card-trend ${parseFloat(card.trend) >= 0 ? 'positive' : 'negative'}`}>
                            {parseFloat(card.trend) >= 0 ? <FaArrowUp /> : <FaArrowDown />}
                            {Math.abs(parseFloat(card.trend))}% <span className="trend-period">vs Last Month</span>
                        </div>
                    </div>
                ))}
            </div>

            {/* 2. Main Charts Grid */}
            <div className="charts-grid">
                <div className="chart-card large">
                    <Bar options={optionsBar} data={dataSales} />
                </div>
                <div className="chart-card medium">
                    <Doughnut options={optionsDoughnut} data={dataProductMix} />
                </div>
                <div className="chart-card large">
                    <Line options={{ ...optionsBar, title: { display: true, text: 'Current Stock Levels by Product' } }} data={dataStockLevels} />
                </div>
            </div>
            
            {/* Placeholder for detailed table/reports (e.g., Low Stock Alerts) */}
            <div className="detailed-reports-card">
                <h2>Low Stock Alerts ({stats.stockLevels.data.filter(d => d <= 20).length} Items)</h2>
                <p>Monitor products where stock is below threshold (20 units) to prevent sales loss.</p>
            </div>
        </div>
    );
}

export default Statics;
